// ============================================================================
// HAND EVALUATION SERVICE - Texas Hold'em Hand Ranking
// ============================================================================
// This service evaluates poker hands and determines winners.
// Uses the unified type definitions from GameState.ts.
// 
// Hand Rankings (best to worst):
// 1. Royal Flush     - A-K-Q-J-10 of same suit
// 2. Straight Flush  - Five consecutive cards of same suit
// 3. Four of a Kind  - Four cards of same rank
// 4. Full House      - Three of a kind + pair
// 5. Flush           - Five cards of same suit
// 6. Straight        - Five consecutive cards
// 7. Three of a Kind - Three cards of same rank
// 8. Two Pair        - Two different pairs
// 9. Pair            - Two cards of same rank
// 10. High Card      - Highest card wins
// ============================================================================

import {
  Card,
  CardRank,
  Hand,
  HandRankType,
  EvaluatedHand,
  RANK_VALUES,
  HAND_RANK_VALUES,
} from '../types/GameState';

// ============================================================================
// HAND EVALUATION SERVICE CLASS
// ============================================================================

class HandEvaluationService {
  // ============================================================================
  // MAIN EVALUATION METHOD
  // ============================================================================

  /**
   * Evaluate the best 5-card hand from hole cards + community cards
   * @param holeCards - Player's 2 hole cards
   * @param communityCards - 5 community cards on the board
   * @returns The best possible hand, or null if not enough cards
   */
  evaluateHand(holeCards: Card[], communityCards: Card[]): Hand | null {
    // Validate inputs
    if (!holeCards || holeCards.length !== 2) {
      console.error('❌ Hand evaluation requires exactly 2 hole cards');
      return null;
    }

    if (!communityCards || communityCards.length < 3) {
      console.error('❌ Hand evaluation requires at least 3 community cards');
      return null;
    }

    const allCards = [...holeCards, ...communityCards];

    // Need at least 5 cards to evaluate
    if (allCards.length < 5) {
      console.error('❌ Need at least 5 cards to evaluate hand');
      return null;
    }

    // Generate all possible 5-card combinations
    const combinations = this.generateCombinations(allCards, 5);

    // Find the best hand
    let bestHand: Hand | null = null;

    for (const combo of combinations) {
      const hand = this.rankHand(combo);

      if (!bestHand || this.compareHands(hand, bestHand) > 0) {
        bestHand = hand;
      }
    }

    if (bestHand) {
      console.log(`✅ Hand evaluated: ${bestHand.rank} [${this.handToString(bestHand)}]`);
    }

    return bestHand;
  }

  /**
   * Evaluate hand and return detailed EvaluatedHand object
   * @param holeCards - Player's 2 hole cards
   * @param communityCards - 5 community cards
   * @returns Detailed hand evaluation
   */
  evaluateHandDetailed(holeCards: Card[], communityCards: Card[]): EvaluatedHand | null {
    const hand = this.evaluateHand(holeCards, communityCards);

    if (!hand) {
      return null;
    }

    return {
      rank: hand.rank,
      value: HAND_RANK_VALUES[hand.rank],
      kickers: hand.tiebreakers,
      bestFiveCards: hand.cards,
    };
  }

  // ============================================================================
  // COMBINATION GENERATION
  // ============================================================================

  /**
   * Generate all combinations of size k from an array of cards
   * Uses recursive algorithm for efficiency
   */
  private generateCombinations(cards: Card[], k: number): Card[][] {
    if (k === 0) return [[]];
    if (cards.length === 0) return [];
    if (cards.length < k) return [];

    const [first, ...rest] = cards;

    // Combinations that include the first card
    const withFirst = this.generateCombinations(rest, k - 1).map(combo => [first, ...combo]);

    // Combinations that don't include the first card
    const withoutFirst = this.generateCombinations(rest, k);

    return [...withFirst, ...withoutFirst];
  }

  // ============================================================================
  // HAND RANKING
  // ============================================================================

  /**
   * Rank a 5-card hand
   * @param cards - Exactly 5 cards
   * @returns Hand object with rank and tiebreakers
   */
  rankHand(cards: Card[]): Hand {
    if (cards.length !== 5) {
      // Return high card for invalid hands
      const sortedCards = [...cards].sort((a, b) => RANK_VALUES[b.rank] - RANK_VALUES[a.rank]);
      return {
        cards: sortedCards,
        rank: 'High Card',
        highCards: sortedCards,
        tiebreakers: sortedCards.map(c => RANK_VALUES[c.rank]),
      };
    }

    // Get card values sorted high to low
    const values = cards.map(c => RANK_VALUES[c.rank]).sort((a, b) => b - a);

    // Check for flush (all same suit)
    const isFlush = this.checkFlush(cards);

    // Check for straight
    const straightHighCard = this.checkStraight(values);
    const isStraight = straightHighCard > 0;

    // Count card ranks
    const rankCounts = this.countRanks(cards);
    const counts = Object.values(rankCounts).sort((a, b) => b - a);

    // Determine hand rank
    let rank: HandRankType;
    let tiebreakers: number[];

    if (isFlush && isStraight) {
      if (straightHighCard === 14) {
        // Royal Flush: A-K-Q-J-10 of same suit
        rank = 'Royal Flush';
        tiebreakers = [14];
      } else {
        // Straight Flush
        rank = 'Straight Flush';
        tiebreakers = [straightHighCard];
      }
    } else if (counts[0] === 4) {
      // Four of a Kind
      rank = 'Four of a Kind';
      tiebreakers = this.getTiebreakersForGroups(rankCounts, 4);
    } else if (counts[0] === 3 && counts[1] === 2) {
      // Full House
      rank = 'Full House';
      tiebreakers = this.getTiebreakersForGroups(rankCounts, 3, 2);
    } else if (isFlush) {
      // Flush
      rank = 'Flush';
      tiebreakers = values;
    } else if (isStraight) {
      // Straight
      rank = 'Straight';
      tiebreakers = [straightHighCard];
    } else if (counts[0] === 3) {
      // Three of a Kind
      rank = 'Three of a Kind';
      tiebreakers = this.getTiebreakersForGroups(rankCounts, 3);
    } else if (counts[0] === 2 && counts[1] === 2) {
      // Two Pair
      rank = 'Two Pair';
      tiebreakers = this.getTiebreakersForTwoPair(rankCounts);
    } else if (counts[0] === 2) {
      // Pair
      rank = 'Pair';
      tiebreakers = this.getTiebreakersForGroups(rankCounts, 2);
    } else {
      // High Card
      rank = 'High Card';
      tiebreakers = values;
    }

    // Sort cards by value for display
    const sortedCards = [...cards].sort((a, b) => RANK_VALUES[b.rank] - RANK_VALUES[a.rank]);

    return {
      cards: sortedCards,
      rank,
      highCards: sortedCards,
      tiebreakers,
    };
  }

  // ============================================================================
  // HAND CHECKING HELPERS
  // ============================================================================

  /**
   * Check if all cards are the same suit (flush)
   */
  private checkFlush(cards: Card[]): boolean {
    const firstSuit = cards[0].suit;
    return cards.every(c => c.suit === firstSuit);
  }

  /**
   * Check for a straight and return the high card value
   * Returns 0 if not a straight
   * Handles wheel (A-2-3-4-5) as 5-high straight
   */
  private checkStraight(values: number[]): number {
    // Check for regular straight (consecutive values)
    let isRegularStraight = true;
    for (let i = 0; i < values.length - 1; i++) {
      if (values[i] - values[i + 1] !== 1) {
        isRegularStraight = false;
        break;
      }
    }

    if (isRegularStraight) {
      return values[0]; // High card of straight
    }

    // Check for wheel (A-2-3-4-5)
    // Values would be [14, 5, 4, 3, 2]
    if (
      values[0] === 14 &&
      values[1] === 5 &&
      values[2] === 4 &&
      values[3] === 3 &&
      values[4] === 2
    ) {
      return 5; // Wheel is 5-high
    }

    return 0; // Not a straight
  }

  /**
   * Count occurrences of each rank
   */
  private countRanks(cards: Card[]): { [rank: string]: number } {
    const counts: { [rank: string]: number } = {};
    for (const card of cards) {
      counts[card.rank] = (counts[card.rank] || 0) + 1;
    }
    return counts;
  }

  // ============================================================================
  // TIEBREAKER CALCULATION
  // ============================================================================

  /**
   * Get tiebreakers for hands with groups (pairs, trips, quads)
   * Orders by: group size (descending), then value (descending)
   */
  private getTiebreakersForGroups(
    rankCounts: { [rank: string]: number },
    ...groupSizes: number[]
  ): number[] {
    const groups: { rank: string; count: number; value: number }[] = [];

    for (const [rank, count] of Object.entries(rankCounts)) {
      groups.push({
        rank,
        count,
        value: RANK_VALUES[rank as CardRank],
      });
    }

    // Sort by count (descending) then by value (descending)
    groups.sort((a, b) => {
      if (a.count !== b.count) return b.count - a.count;
      return b.value - a.value;
    });

    return groups.map(g => g.value);
  }

  /**
   * Get tiebreakers specifically for two pair
   * Format: [higher pair value, lower pair value, kicker]
   */
  private getTiebreakersForTwoPair(rankCounts: { [rank: string]: number }): number[] {
    const pairs: number[] = [];
    let kicker = 0;

    for (const [rank, count] of Object.entries(rankCounts)) {
      const value = RANK_VALUES[rank as CardRank];
      if (count === 2) {
        pairs.push(value);
      } else {
        kicker = value;
      }
    }

    // Sort pairs descending
    pairs.sort((a, b) => b - a);

    return [...pairs, kicker];
  }

  // ============================================================================
  // HAND COMPARISON
  // ============================================================================

  /**
   * Compare two hands
   * @returns positive if hand1 wins, negative if hand2 wins, 0 if tie
   */
  compareHands(hand1: Hand, hand2: Hand): number {
    // Compare hand ranks first
    const rank1 = HAND_RANK_VALUES[hand1.rank];
    const rank2 = HAND_RANK_VALUES[hand2.rank];

    if (rank1 !== rank2) {
      return rank1 - rank2;
    }

    // Same rank - compare tiebreakers
    const len = Math.min(hand1.tiebreakers.length, hand2.tiebreakers.length);

    for (let i = 0; i < len; i++) {
      if (hand1.tiebreakers[i] !== hand2.tiebreakers[i]) {
        return hand1.tiebreakers[i] - hand2.tiebreakers[i];
      }
    }

    // Hands are equal
    return 0;
  }

  /**
   * Compare multiple hands and return winner indices
   * Handles ties by returning all tying indices
   */
  findWinners(hands: (Hand | null)[]): number[] {
    const validHands = hands
      .map((hand, index) => ({ hand, index }))
      .filter(h => h.hand !== null) as { hand: Hand; index: number }[];

    if (validHands.length === 0) {
      return [];
    }

    if (validHands.length === 1) {
      return [validHands[0].index];
    }

    // Find the best hand
    let bestHand = validHands[0].hand;
    for (const { hand } of validHands) {
      if (this.compareHands(hand, bestHand) > 0) {
        bestHand = hand;
      }
    }

    // Find all hands that tie with the best
    const winners = validHands
      .filter(({ hand }) => this.compareHands(hand, bestHand) === 0)
      .map(({ index }) => index);

    return winners;
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Convert a hand to a readable string
   */
  handToString(hand: Hand): string {
    return hand.cards.map(c => `${c.rank}${this.suitToSymbol(c.suit)}`).join(' ');
  }

  /**
   * Convert suit to symbol
   */
  private suitToSymbol(suit: string): string {
    const symbols: { [key: string]: string } = {
      hearts: '♥',

      diamonds: '♦',

      clubs: '♣',

      spades: '♠',

    };
    return symbols[suit] || suit.charAt(0).toUpperCase();
  }

  /**
   * Get a human-readable description of a hand
   */
  getHandDescription(hand: Hand): string {
    const highCard = hand.highCards[0];
    const highCardName = this.rankToName(highCard.rank);

    switch (hand.rank) {
      case 'Royal Flush':
        return 'Royal Flush';
      case 'Straight Flush':
        return `Straight Flush, ${highCardName} high`;
      case 'Four of a Kind':
        return `Four of a Kind, ${this.rankToName(this.findGroupRank(hand, 4))}s`;
      case 'Full House':
        const trips = this.findGroupRank(hand, 3);
        const pair = this.findGroupRank(hand, 2);
        return `Full House, ${this.rankToName(trips)}s full of ${this.rankToName(pair)}s`;
      case 'Flush':
        return `Flush, ${highCardName} high`;
      case 'Straight':
        return `Straight, ${highCardName} high`;
      case 'Three of a Kind':
        return `Three of a Kind, ${this.rankToName(this.findGroupRank(hand, 3))}s`;
      case 'Two Pair':
        const pairs = this.findPairRanks(hand);
        return `Two Pair, ${this.rankToName(pairs[0])}s and ${this.rankToName(pairs[1])}s`;
      case 'Pair':
        return `Pair of ${this.rankToName(this.findGroupRank(hand, 2))}s`;
      case 'High Card':
        return `High Card, ${highCardName}`;
      default:
        return hand.rank;
    }
  }

  /**
   * Find the rank that appears a specific number of times
   */
  private findGroupRank(hand: Hand, count: number): CardRank {
    const rankCounts = this.countRanks(hand.cards);
    for (const [rank, c] of Object.entries(rankCounts)) {
      if (c === count) {
        return rank as CardRank;
      }
    }
    return hand.highCards[0].rank;
  }

  /**
   * Find both pair ranks for two pair
   */
  private findPairRanks(hand: Hand): CardRank[] {
    const rankCounts = this.countRanks(hand.cards);
    const pairs: { rank: CardRank; value: number }[] = [];

    for (const [rank, count] of Object.entries(rankCounts)) {
      if (count === 2) {
        pairs.push({
          rank: rank as CardRank,
          value: RANK_VALUES[rank as CardRank],
        });
      }
    }

    // Sort by value descending
    pairs.sort((a, b) => b.value - a.value);

    return pairs.map(p => p.rank);
  }

  /**
   * Convert rank to full name
   */
  private rankToName(rank: CardRank): string {
    const names: { [key: string]: string } = {
      '2': 'Two',
      '3': 'Three',
      '4': 'Four',
      '5': 'Five',
      '6': 'Six',
      '7': 'Seven',
      '8': 'Eight',
      '9': 'Nine',
      '10': 'Ten',
      'J': 'Jack',
      'Q': 'Queen',
      'K': 'King',
      'A': 'Ace',
    };
    return names[rank] || rank;
  }

  /**
   * Get the numeric value of a hand for quick comparison
   * Higher value = better hand
   */
  getHandValue(hand: Hand): number {
    // Base value from hand rank (multiply by large number to ensure rank dominates)
    let value = HAND_RANK_VALUES[hand.rank] * 10000000;

    // Add tiebreaker values (each successive tiebreaker worth less)
    for (let i = 0; i < hand.tiebreakers.length && i < 5; i++) {
      value += hand.tiebreakers[i] * Math.pow(15, 4 - i);
    }

    return value;
  }

  /**
   * Check if a specific hand type is possible with given cards
   * Useful for calculating drawing odds
   */
  canMakeHand(
    holeCards: Card[],
    communityCards: Card[],
    targetRank: HandRankType
  ): boolean {
    const hand = this.evaluateHand(holeCards, communityCards);
    if (!hand) return false;
    return HAND_RANK_VALUES[hand.rank] >= HAND_RANK_VALUES[targetRank];
  }

  /**
   * Get possible outs (cards that improve the hand)
   * This is a simplified version - full implementation would consider all scenarios
   */
  getOuts(holeCards: Card[], communityCards: Card[]): number {
    // This would require a more complex implementation
    // counting cards that would improve the hand
    // For now, return 0 as placeholder
    return 0;
  }
}

// ============================================================================
// EXPORT
// ============================================================================

export default new HandEvaluationService();